import { useState, useEffect } from "react";
import { useLanguage } from "../LanguageContext";
import { AppBar, Box, Button, Drawer, List, ListItem, ListItemText, IconButton, Typography, MenuItem, Menu } from "@mui/material";
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useNavigate, useLocation } from "react-router-dom";
import mainLogo from "../img/logo.png";
import illyesLogo from "../img/illyes_szk.png";
import pteLogo from "../img/PTElogo.png";
import kitLogoEN from "../img/KIT_LOGO_EN.png";
import kitLogoHU from "../img/KIT_LOGO_HU.png";
import decentrumLogo from "../img/decentrum.png";

const pages = {
  en: [
    //{ name: "Sponsors", path: "/sponsors" },
    { name: "Deadlines", path: "/deadlines" },
    { name: "Host", path: "/host" },
    { name: "Contact", path: "/contact" },
  ],
  hu: [
    //{ name: "Szponzorok", path: "/sponsors" },
    { name: "Határidők", path: "/deadlines" },
    { name: "Szervező", path: "/host" },
    { name: "Kapcsolat", path: "/contact" },
  ],
};

const programItems = {
  en: [
    { name: "Hungarian Program", path: "/hungarian-program" },
    { name: "German Program", path: "/german-program" },
    { name: "English Program", path: "/english-program" },
    /*{ name: "Speakers & Exhibitors", path: "/speakers-exhibitors" },
    { name: "Call for Paper", path: "/call-for-paper" },*/
  ],
  hu: [
    { name: "Magyar Program", path: "/hungarian-program" },
    { name: "Német Program", path: "/german-program" },
    { name: "Angol Program", path: "/english-program" },
    /*{ name: "Eladók & Kiállítók", path: "/speakers-exhibitors" },
    { name: "Konferenciafelhívás", path: "/call-for-paper" },*/
  ],
};

const mediaItems = {
  en: [
    { name: "Photos & Videos", path: "/gallery" },
    //{ name: "News", path: "/news" },
    { name: "Publications", path: "/publications" },
  ],
  hu: [
    { name: "Fotók & Videók", path: "/gallery" },
    //{ name: "Hírek", path: "/news" },
    { name: "Publikációk", path: "/publications" },
  ],
};

export default function NavigationBar({ isMobile }: { isMobile: boolean }) {
  const backgroundColor = "white";
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({
    program: null,
    media: null,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenMenu = (menu: string) => (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl({ ...menuAnchorEl, [menu]: event.currentTarget });
  };

  const handleCloseMenu = (menu: string) => () => {
    setMenuAnchorEl({ ...menuAnchorEl, [menu]: null });
  };

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const updatePathOnLanguageToggle = () => {
    const currentPath = location.pathname.replace(/^\/(en|hu)/, "");
    const newPath = `/${language}${currentPath}`;
    navigate(newPath);
  };

  useEffect(() => {
    updatePathOnLanguageToggle();
  }, [language]);

  const LanguageSwitcher = () => (
    <Typography component="span" sx={{ ml: 2, fontWeight: "bold", color: "#000000" }}>
      {language === "hu" ? (
        <>
          <span style={{ textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}>HU</span>
          {" / "}
          <span style={{ cursor: "pointer" }} onClick={toggleLanguage}>EN</span>
        </>
      ) : (
        <>
          <span style={{ cursor: "pointer" }} onClick={toggleLanguage}>HU</span>
          {" / "}
          <span style={{ textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}>EN</span>
        </>
      )}
    </Typography>
  );

  return (
    <Box>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: backgroundColor,
          boxShadow: "none",
          transition: "background-color 0.3s ease",
          top: 0,
          zIndex: 10,
        }}>
        {isMobile
          ? MobileView(toggleDrawer, drawerOpen, LanguageSwitcher, navigate, language)
          : DesktopView(menuAnchorEl, handleOpenMenu, handleCloseMenu, LanguageSwitcher, navigate, language)}
      </AppBar>
    </Box>
  );
}

function MobileView(
  toggleDrawer: (open: boolean) => () => void,
  drawerOpen: boolean,
  LanguageSwitcher: () => JSX.Element,
  navigate: (path: string) => void,
  language: "en" | "hu"
) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const conferenceDate = new Date("2025-05-09T08:30:00");
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = conferenceDate.getTime() - now.getTime();

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);

      setTimeLeft({ days, hours, minutes });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px",
        }}>
        {/* Left Section: Conference Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button onClick={() => navigate(`/${language}/home`)} sx={{ padding: 0 }}>
            <img src={mainLogo} alt="Play & Society" style={{ height: "100px" }} />
          </Button>

          {/* Counter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "5px",
              border: "2px solid #000000",
              borderRadius: "5px",
              color: "#000000",
              width: "150px",
              height: "70px",
            }}>
            {/*<Box sx={{ textAlign: "center", marginRight: "10px" }}>
              <Typography variant="caption">{timeLeft.days}</Typography>
              <Typography variant="body2">{language === "en" ? "D" : "N"}</Typography>
            </Box>
            <Box sx={{ textAlign: "center", marginRight: "10px" }}>
              <Typography variant="caption">{timeLeft.hours}</Typography>
              <Typography variant="body2">{language === "en" ? "H" : "Ó"}</Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption">{timeLeft.minutes}</Typography>
              <Typography variant="body2">{language === "en" ? "M" : "P"}</Typography>
            </Box>*/}
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#000000", textTransform: "uppercase" }}>
                {language === "en" ? "9-10 May 2025" : "2025. Május 9-10"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Section: Menu Icon */}
        <IconButton
          size="large"
          edge="end"
          sx={{ color: "#cc0000", "&:hover": { color: "#a20000" } }}
          onClick={toggleDrawer(true)}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Drawer for Mobile Menu */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: '300px', backgroundColor: '#f8f8f8' } }}>
        <Box sx={{ padding: "15px" }}>
          {/* Logos in Drawer */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mb: 2 }}>
            <img src={pteLogo} alt="pte" style={{ height: "40px" }} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <img src={illyesLogo} alt="illyes" style={{ height: "50px" }} />
              <img src={language === "en" ? kitLogoEN : kitLogoHU} alt="kit" style={{ height: "50px" }} />
              <img src={decentrumLogo} alt="decentrum" style={{ height: "50px" }} />
            </Box>
          </Box>

          {/* Menu Items */}
          <List sx={{ '& .MuiListItemText-primary': { fontFamily: 'Roboto, sans-serif', fontSize: '1rem', color: '#000' } }}>
            <ListItem button onClick={() => { navigate(`/${language}/home`); toggleDrawer(false)(); }}>
              <ListItemText primary={language === "en" ? "Home" : "Kezdőlap"} />
            </ListItem>

            {/* Program Section */}
            <ListItem sx={{ fontWeight: "bold", py: 1 }}>
              <ListItemText primary={language === "en" ? "Program" : "Program"} sx={{ '& .MuiTypography-root': { fontWeight: 'bold' } }} />
            </ListItem>
            {programItems[language].map((item) => (
              <ListItem
                button
                key={item.name}
                onClick={() => { navigate(`/${language}${item.path}`); toggleDrawer(false)(); }}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={item.name} />
              </ListItem>
            ))}

            {/* Main Pages */}
            {pages[language].map((page) => (
              <ListItem
                button
                key={page.name}
                onClick={() => { navigate(`/${language}${page.path}`); toggleDrawer(false)(); }}>
                <ListItemText primary={page.name} />
              </ListItem>
            ))}

            {/* Media Section */}
            <ListItem sx={{ fontWeight: "bold", py: 1 }}>
              <ListItemText primary={language === "en" ? "Media" : "Média"} sx={{ '& .MuiTypography-root': { fontWeight: 'bold' } }} />
            </ListItem>
            {mediaItems[language].map((item) => (
              <ListItem
                button
                key={item.name}
                onClick={() => { navigate(`/${language}${item.path}`); toggleDrawer(false)(); }}
                sx={{ pl: 4 }}
              >
                <ListItemText primary={item.name} />
              </ListItem>
            ))}

            {/* Registration */}
            {/*<ListItem
              button
              onClick={() => { window.open("https://forms.gle/amt1VnxKCj1EaRadA", "_blank"); toggleDrawer(false)(); }}>
              {/*onClick={() => { navigate(`/${language}/register`); toggleDrawer(false)(); }}>*/}
              {/*<ListItemText primary={language === "en" ? "Registration" : "Regisztráció"} />
            </ListItem>*/}

            {/* Language Switcher */}
            <ListItem>
              <LanguageSwitcher />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}


function DesktopView(
  menuAnchorEl: { [key: string]: HTMLElement | null },
  handleOpenMenu: (menu: string) => (event: React.MouseEvent<HTMLElement>) => void,
  handleCloseMenu: (menu: string) => () => void,
  LanguageSwitcher: () => JSX.Element,
  navigate: (path: string) => void,
  language: "en" | "hu"
) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const conferenceDate = new Date("2025-05-09T08:30:00");
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = conferenceDate.getTime() - now.getTime();

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);

      setTimeLeft({ days, hours, minutes });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* Top Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "15px 30px",
          borderBottom: "1px solid #ccc",
        }}>

        {/* Logo and Language Switcher */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <Button onClick={() => navigate(`/${language}/home`)} sx={{ p: 0 }}>
            <img src={mainLogo} alt="Play & Society" style={{ height: "100px" }} />
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", color: "#000000", fontWeight: "bold" }}>
            <LanguageIcon />
            <LanguageSwitcher />
          </Box>
        </Box>

        {/* Counter and Date */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            border: "2px solid #000000",
            borderRadius: "5px",
            padding: "10px 20px",
            color: "#000000",
          }}>
          <Box sx={{ display: "flex", gap: "10px" }}>
            {/*<Box sx={{ textAlign: "center" }}>
              <Typography variant="h5">{timeLeft.days}</Typography>
              <Typography variant="caption">{language === "en" ? "Days" : "Nap"}</Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5">{timeLeft.hours}</Typography>
              <Typography variant="caption">{language === "en" ? "Hours" : "Óra"}</Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5">{timeLeft.minutes}</Typography>
              <Typography variant="caption">{language === "en" ? "Minutes" : "Perc"}</Typography>
            </Box>*/}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#000000", textTransform: "uppercase" }}>
            {language === "en" ? "9-10 May 2025" : "2025. Május 9-10"}
          </Typography>
        </Box>

        {/* Right Logos */}
        <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <img src={decentrumLogo} alt="Decentrum Logo" style={{ height: "100px" }} />
          <img src={illyesLogo} alt="Illyes Logo" style={{ height: "100px" }} />
          <img src={language === "en" ? kitLogoEN : kitLogoHU} alt="KIT Logo" style={{ height: "100px" }} />
          <img src={pteLogo} alt="PTE Logo" style={{ height: "100px" }} />
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
          padding: "15px 0",
          backgroundColor: "#f8f8f8",
        }}>
        <Button
          onClick={() => navigate(`/${language}/home`)}
          sx={{ color: "#000000", fontWeight: "bold", fontSize: "1rem", textTransform: "uppercase" }}>
          {language === "en" ? "Home" : "Kezdőlap"}
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            onClick={handleOpenMenu("program")}
            sx={{ color: "#000000", fontWeight: "bold", fontSize: "1rem", textTransform: "uppercase" }}
            endIcon={<ArrowDropDownIcon />}>
            {language === "en" ? "Program" : "Program"}
          </Button>
          <Menu
            anchorEl={menuAnchorEl["program"]}
            open={Boolean(menuAnchorEl["program"])}
            onClose={handleCloseMenu("program")}
            disablePortal
            disableScrollLock
            sx={{ mt: 1 }}>
            {programItems[language].map((item) => (
              <MenuItem
                key={item.name}
                onClick={() => { handleCloseMenu("program")(); navigate(`/${language}${item.path}`); }}>
                {item.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Other Pages */}
        {pages[language].map((page) => (
          <Button
            key={page.name}
            onClick={() => navigate(`/${language}${page.path}`)}
            sx={{ color: "#000000", fontWeight: "bold", fontSize: "1rem", textTransform: "uppercase" }}>
            {page.name}
          </Button>
        ))}

        {/* Media Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Button
            onClick={handleOpenMenu("media")}
            sx={{ color: "#000000", fontWeight: "bold", fontSize: "1rem", textTransform: "uppercase" }}
            endIcon={<ArrowDropDownIcon />}>
            {language === "en" ? "Media" : "Média"}
          </Button>
          <Menu
            anchorEl={menuAnchorEl["media"]}
            open={Boolean(menuAnchorEl["media"])}
            onClose={handleCloseMenu("media")}
            disablePortal
            disableScrollLock
            sx={{ mt: 1 }}>
            {mediaItems[language].map((item) => (
              <MenuItem
                key={item.name}
                onClick={() => { handleCloseMenu("media")(); navigate(`/${language}${item.path}`); }}>
                {item.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Registration Button */}
        {/*<Button
          variant="contained"
          sx={{
            backgroundColor: "#cc0000",
            color: "#ffffff",
            "&:hover": { backgroundColor: "#a20000" },
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
          onClick={() => window.open("https://forms.gle/amt1VnxKCj1EaRadA", "_blank")}>*/}
          {/*onClick={() => navigate(`/${language}/register`)}>*/}
          {/*{language === "en" ? "Registration" : "Regisztráció"}
        </Button>*/}
      </Box>
    </Box>
  );
}